/**
 * use-nav-context — builds the nav-model's `NavContext` REACTIVELY:
 *
 *   - per-tab session (`useSession`): logged/guest + platform role (F2-C);
 *   - role in the ACTIVE org (`useActiveOrg` + `useMyOrgRole`): an org or
 *     account switch re-scopes visibility without reloading;
 *   - guardian (own guardian_link rows) and teacher (own cohort_teacher
 *     rows, synced by the `my_cohorts_teacher` stream) — local-first.
 *
 * Everything is reactive signals/queries: the sidebar re-evaluates the
 * matrix on the fly.
 *
 * SINGLE INSTANCE. Building it costs four queries, and two of them open
 * their own PowerSync `watch` (with its own table resolution and listener),
 * so every extra call is duplicated work nobody sees. That's why the shell
 * mounts `<NavContextProvider>` once and `useNavContext()` is a CONSUMER: if
 * it finds no provider it throws, instead of building its own set and
 * silently reintroducing the duplication.
 */
import { type Accessor, createContext, createMemo, type ParentProps, useContext } from "solid-js";
import { cohort_teacher, eq } from "@space/powersync";
// FLAGS: item gating by feature (active org) — same resolver as routes.
import {
  cohortQueryKeys,
  featureGraph,
  parseFeatureOverrides,
  resolveFeatureEffective,
} from "@space/shared";
import { createPowerSyncSingle } from "@space/shared/solid";
import { useSession } from "@/lib/auth";
import { usePowerSyncContext } from "@/providers/power-sync-context";
import { useDrizzle } from "@/hooks/use-drizzle";
import { useActiveOrg } from "@/features/academy/hooks/use-active-org";
import { useMyGuardianWards, useMyOrgRole } from "@/features/academy/hooks/use-people-data";
import { useOrgSettings } from "@/features/academy/hooks/use-academy-data";
import { usePlatformFlags } from "@/features/flags/use-feature-enabled";
import { OFFLINE_CACHE_STALE_TIME_MS } from "@/lib/constants";
import type { NavContext } from "./nav-model";

const NavContextChannel = createContext<Accessor<NavContext>>();

/**
 * Mounts the navigation context ONCE for the whole shell. Goes as high as
 * possible in the `_app` tree: the sidebar, the per-route help link and
 * "My profiles" read the same computation.
 */
export function NavContextProvider(props: ParentProps) {
  const context = createNavContext();
  return <NavContextChannel.Provider value={context}>{props.children}</NavContextChannel.Provider>;
}

/** Shared navigation context. Requires `<NavContextProvider>` above it. */
export function useNavContext(): Accessor<NavContext> {
  const context = useContext(NavContextChannel);
  if (!context) {
    // A fallback that built its own context "just in case" would be exactly
    // the duplication this provider eliminates, and silently at that.
    throw new Error("useNavContext requiere <NavContextProvider> (shell de _app)");
  }
  return context;
}

function createNavContext(): Accessor<NavContext> {
  const session = useSession();
  const { db } = usePowerSyncContext();
  const drizzle = useDrizzle();
  const { activeOrgId } = useActiveOrg();
  const myOrgRole = useMyOrgRole(activeOrgId);
  const wards = useMyGuardianWards();

  // Do I have my own cohort_teacher rows? (teacher assigned to some cohort,
  // even if their member.role isn't `teacher`). A single row is enough.
  const teacherRow = createPowerSyncSingle<{ id: string }>({
    queryKey: () => cohortQueryKeys.myTeaching(session.userId()),
    db,
    queryFn: () => {
      const d = drizzle();
      const uid = session.userId();
      return d && uid
        ? d
            .select({ id: cohort_teacher.id })
            .from(cohort_teacher)
            .where(eq(cohort_teacher.user_id, uid))
            .limit(1)
        : null;
    },
    enabled: () => Boolean(session.userId()) && Boolean(drizzle()),
    staleTime: OFFLINE_CACHE_STALE_TIME_MS,
    suspense: false,
  });

  // FLAGS: effective resolver (graph included) over flags + active org.
  const platformFlags = usePlatformFlags();
  const orgSettings = useOrgSettings(() => activeOrgId());
  const graph = featureGraph();
  const featureEnabled = createMemo(() => {
    const platformMap = platformFlags();
    const org = parseFeatureOverrides(orgSettings.data?.features);
    return (key: string): boolean =>
      resolveFeatureEffective(key, { platformMap, org }, graph).enabled;
  });

  const context = createMemo<NavContext, NavContext | undefined>(
    () => {
      const isLoggedIn = session.isAuthenticated();
      return {
        isLoggedIn,
        featureEnabled: featureEnabled(),
        platformRole: session.data?.user.role ?? null,
        // No real session means no org role (a guest never inherits the
        // global user's org). `useMyOrgRole` normalizes an absent
        // membership → student, which opens no section.
        orgRole: isLoggedIn ? myOrgRole.role() : null,
        isTeacher: isLoggedIn && (myOrgRole.role() === "teacher" || Boolean(teacherRow.data)),
        isGuardian: isLoggedIn && (wards.data?.length ?? 0) > 0,
      };
    },
    undefined,
    // The context is RECOMPUTED every time one of its sources emits, but
    // only PROPAGATES if it truly changed. Without this, the session store
    // notifies on every navigation (same value, different tick) and the new
    // object reached the sidebar as "something changed": `<For>` would
    // unmount and remount all links, and the menu flickered on navigation
    // even without a session.
    { equals: sameNavContext },
  );
  return context;
}

/**
 * SEMANTIC equality of the navigation context. `featureEnabled` is compared
 * by reference on purpose: it's a memoized closure over flags + org
 * overrides, so its identity only changes when one of the two changes.
 */
function sameNavContext(a: NavContext | undefined, b: NavContext): boolean {
  // First computation: there's no previous context, so it's never "equal".
  if (a === undefined) {
    return false;
  }
  return (
    a.isLoggedIn === b.isLoggedIn &&
    a.platformRole === b.platformRole &&
    a.orgRole === b.orgRole &&
    a.isTeacher === b.isTeacher &&
    a.isGuardian === b.isGuardian &&
    a.featureEnabled === b.featureEnabled
  );
}
